<template>
  <div class="ms-4 mt-3 mb-3">
    <p class="text-xl fw-bold mb-0">Compliance Centre</p>
    <p class="text-xs text-dark mb-0">
      Monitor trust compliance status, manage documents, and ensure regulatory adherence.
    </p>
  </div>
  <div class="compliance-centre px-4 p-2">
    <div class="row">
      <!-- Heat Map -->
      <div class="col-12 col-md-6 mb-3">
        <div class="card shadow p-3 rounded-lg">
          <p class="text-x fw-bold mb-2">Compliance Status Heat Map</p>
          <p class="text-muted text-xs mb-2">Year-on-year compliance overview by quarter</p>

          <!-- Legend -->
          <div class="d-flex gap-3 mb-3 mt-3 align-items-center flex-wrap">
            <span class="legend-item text-dark text-xxs">
              <span class="legend-dot bg-fully"></span> Fully Compliant
            </span>
            <span class="legend-item text-dark text-xxs">
              <span class="legend-dot bg-partial"></span> Partially Compliant
            </span>
            <span class="legend-item text-dark text-xxs">
              <span class="legend-dot bg-non"></span> Non-Compliant
            </span>
            <span class="legend-item text-dark text-xxs">
              <span class="legend-dot bg-pending"></span> Pending Review
            </span>
          </div>

          <div class="table-responsive">
            <table class="table table-lined align-middle text-sm">
              <thead>
                <tr>
                  <th>Year</th>
                  <th>Q1</th>
                  <th>Q2</th>
                  <th>Q3</th>
                  <th>Q4</th>
                  <th class="text-end">Score</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in heat_map" :key="row.year">
                  <td class="fw-bold">{{ row.year }}</td>
                  <td><span class="heat" :class="heat_class(row.q1)"></span></td>
                  <td><span class="heat" :class="heat_class(row.q2)"></span></td>
                  <td><span class="heat" :class="heat_class(row.q3)"></span></td>
                  <td><span class="heat" :class="heat_class(row.q4)"></span></td>
                  <td class="text-end fw-bold">{{ row.score }}%</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Integrity Panel -->
      <div class="col-12 col-md-6 mb-3">
        <div class="card shadow p-3 rounded-lg">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <div>
              <p class="text-x fw-bold">Trust Integrity Status</p>
              <p class="text-muted text-xs">Ensuring the Trust is not an Alter Ego or Sham Trust</p>
            </div>
            <div class="text-end">
              <div class="fs-5 fw-bold text-warning">{{ integrity_score }}%</div>
              <small class="text-muted">Integrity Score</small>
            </div>
          </div>

          <div class="alert alert-info mb-4 text-xs mt-3">
            <strong>Why is this important?</strong><br />
            A trust must operate independently from its founder to maintain legal standing.
          </div>

          <div
            v-for="item in integrity_checks"
            :key="item.id"
            class="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center border rounded-lg p-3 mb-2"
            :class="status_class(item.status)"
          >
            <div>
              <p class="fw-bold text-xs mb-1">{{ item.title }}</p>
              <p class="text-muted small mb-0 text-xs">{{ item.description }}</p>
            </div>
            <div class="text-end small text-xs mt-2 mt-md-0">
              <span class="fw-bold">
                {{ item.status === 'passed' ? 'Passed' : 'Review Needed' }}
              </span><br />
              <span class="text-muted text-xs">Checked : {{ item.checked }}</span>
            </div>
          </div>

        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "ComplianceCentre",

  data() {
    return {
      heat_map: [
        { year: 2024, q1: "green", q2: "green", q3: "yellow", q4: "gray", score: 85 },
        { year: 2023, q1: "green", q2: "green", q3: "green", q4: "green", score: 100 },
        { year: 2022, q1: "green", q2: "yellow", q3: "green", q4: "green", score: 92 },
        { year: 2021, q1: "green", q2: "green", q3: "red", q4: "yellow", score: 68 },
      ],

      integrity_score: 83,

      integrity_checks: [
        {
          id: 1,
          title: "Independent Trustee Decisions",
          description: "Trustees make decisions independently without founder influence",
          status: "passed",
          checked: "15 Jan 2024"
        },
        {
          id: 2,
          title: "Documented Meeting Minutes",
          description: "All trustee meetings are properly documented",
          status: "passed",
          checked: "15 Jan 2024"
        },
        {
          id: 3,
          title: "Separate Bank Accounts",
          description: "Trust maintains separate accounts from founder personal accounts",
          status: "passed",
          checked: "15 Jan 2024"
        },
        {
          id: 4,
          title: "Beneficiary Distributions",
          description: "Distributions are made as per trust deed",
          status: "review",
          checked: "15 Jan 2024"
        },
        {
          id: 5,
          title: "Founder Non-Benefit",
          description: "Founder does not receive undue benefit from the trust",
          status: "passed",
          checked: "15 Jan 2024"
        }
      ]
    };
  },

  methods: {
    heat_class(val) {
      return {
        green: "heat-green",
        yellow: "heat-yellow",
        red: "heat-red",
        gray: "heat-gray"
      }[val];
    },

    status_class(status) {
      return status === "passed"
        ? "bg-success-subtle border-success"
        : "bg-warning-subtle border-warning";
    }
  }
};
</script>

<style scoped>
.legend-item {
  display: flex;
  align-items: center;
  gap: 6px;
}

.legend-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  display: inline-block;
}

/* Heat Map colors */
.bg-fully { background-color: #38b000; }
.bg-partial { background-color: #f5a623; }
.bg-non { background-color: #ff3b30; }
.bg-pending { background-color: #e5e5e5; }


.heat {
  display: inline-block;
  width: 48px;
  height: 20px;
  border-radius: 6px;
}

.heat-green { background: #3ccf91; }
.heat-yellow { background: #f7b733; }
.heat-red { background: #e74c3c; }
.heat-gray { background: #ecf0f1; }

/* Tablet adjustments */
@media (max-width: 1024px) {
  .compliance-centre {
    padding: 1rem !important;
  }

  .heat {
    width: 36px;
    height: 18px;
  }

  .legend-item {
    font-size: 0.75rem;
  }
}

@media (max-width: 768px) {
  .heat {
    width: 30px;
    height: 16px;
  }

  .text-x {
    font-size: 0.95rem;
  }
}
</style>
